import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debugLog } from '../../services/common/miscUtils';
import { RootState } from '../../services/redux';
import { loadingActions } from '../../services/redux/features/loading.slice';
import {
  setConversationDocumentMetadata,
  setConversations,
  setCurrentConversation,
  setCurrentConversationMessages,
  setIntent,
  setIsFetchingConversationsLoading,
  setIsSendingMessage,
  setLastConversationFetchError,
} from '../../services/redux/features/notes.slice';
import { ConversationService } from './conversationService';
import { fetchObjectMetadata, getReferenceData } from './conversationUtils';
import { FilterValues } from './Filters';

export interface GroupedMessageSet {
  participantId: string;
  author: string;
  userRole: MilesPosNotesIntegrationService.Components.Schemas.ParticipantType;
  isCurrentUser: boolean;
  messages: MilesPosNotesIntegrationService.Components.Schemas.ConversationMessage[];
}

// Helper function to sort conversations by date
const sortByDate = (
  a: MilesPosNotesIntegrationService.Components.Schemas.Conversation,
  b: MilesPosNotesIntegrationService.Components.Schemas.Conversation
): number => {
  const timeStampA = a.lastMessage?.timeStamp;
  const timeStampB = b.lastMessage?.timeStamp;

  if (timeStampA && timeStampB) {
    return new Date(timeStampB).getTime() - new Date(timeStampA).getTime();
  }
  if (timeStampB) {
    return 1; // B is more recent or A is null/undefined
  }
  if (timeStampA) {
    return -1; // A is more recent or B is null/undefined
  }
  return 0; // Both are null/undefined
};

export function useConversations() {
  const dispatch = useDispatch();

  const conversations = useSelector((state: RootState) => state.notes.conversations);
  const activeConversation = useSelector((state: RootState) => state.notes.currentConversation);

  const [isUpdatingTitle, setIsUpdatingTitle] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchConversations = useCallback(
    async (userInputFilters: FilterValues) => {
      dispatch(setIsFetchingConversationsLoading(true));
      dispatch(setLastConversationFetchError(null));
      dispatch(loadingActions.setLoading(true));
      try {
        const queryFilter: MilesPosNotesIntegrationService.Components.Schemas.FindConversationsRequest = {
          statuses: [userInputFilters.status],
          title: userInputFilters.title,
          ...(userInputFilters.humanReadableObjectReferenceId && userInputFilters.objectType
            ? {
                references: [
                  {
                    label:
                      `${userInputFilters.objectType}_REFERENCE` as MilesPosNotesIntegrationService.Components.Schemas.ReferenceLabelEnum,
                    values: [userInputFilters.humanReadableObjectReferenceId],
                  },
                ],
              }
            : {}),
          ...(userInputFilters.objectType && !userInputFilters.humanReadableObjectReferenceId
            ? { contextRepresentations: [userInputFilters.objectType] }
            : {}),
        };

        if (userInputFilters.isParticipant === 'yes') {
          queryFilter.isParticipant = true;
        } else if (userInputFilters.isParticipant === 'no') {
          queryFilter.isParticipant = false;
        }

        if (userInputFilters.objectType && userInputFilters.humanReadableObjectReferenceId) {
          const metadata = await fetchObjectMetadata(
            userInputFilters.humanReadableObjectReferenceId,
            userInputFilters.objectType
          );

          dispatch(
            setConversationDocumentMetadata({
              entityFullName: metadata.entityFullName ?? '',
              humanReadableObjectReferenceId: userInputFilters.humanReadableObjectReferenceId ?? '',
              objectType: userInputFilters.objectType,
              systemObjectId: metadata.systemObjectId ?? '',
            })
          );

          // Special case for Etudes, that rely on creditApplicationId
          if (userInputFilters.objectType === 'CREDIT_APPLICATION' && metadata.systemObjectId) {
            queryFilter.references = [
              {
                label: 'CREDIT_APPLICATION_REFERENCE',
                values: [metadata.systemObjectId],
              },
            ];
          }
        }

        const response = await ConversationService.findConversations(queryFilter);
        const fetchedConversations = response.conversations ?? [];

        // Filter and sort conversations
        const processedConversations = fetchedConversations
          .filter((c) => {
            if (userInputFilters.sortBy === 'unread') {
              return (c.unreadMessages ?? 0) > 0;
            }
            if (userInputFilters.sortBy === 'read') {
              return (c.unreadMessages ?? 0) === 0;
            }
            return true;
          })
          .filter((c) => !c.referenceMap?.salesQuoteId) // we hide proposition conversations for now
          .filter((c) => {
            if (!userInputFilters.objectType) {
              return true;
            }
            return getReferenceData(c)?.objectType === userInputFilters.objectType;
          })
          .sort(sortByDate);

        dispatch(setConversations(processedConversations));
        if (processedConversations.length > 0) {
          dispatch(setCurrentConversation(processedConversations[0]));
        }
      } catch (error: any) {
        dispatch(setLastConversationFetchError(structuredClone(error?.message)));
        dispatch(setConversations([]));
        dispatch(setCurrentConversation(null));
        dispatch(setConversationDocumentMetadata(null));
        dispatch(setIntent({ type: 'READ', conversationId: null }));
      } finally {
        dispatch(setIsFetchingConversationsLoading(false));
        dispatch(loadingActions.setLoading(false));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, activeConversation]
  );

  const fetchMessages = useCallback(
    async (
      conversationId: string,
      conversationParticipants?: MilesPosNotesIntegrationService.Components.Schemas.Participant[]
    ) => {
      if (!conversationId) return;
      const response = await ConversationService.getMessagesForConversation(conversationId);
      const rawMessages = response.messages?.slice().reverse() ?? [];

      if (rawMessages.length === 0) {
        dispatch(setCurrentConversationMessages([]));
        return;
      }

      const newGroupedMessages: GroupedMessageSet[] = [];
      let currentGroup: GroupedMessageSet | null = null;

      rawMessages.forEach((message) => {
        const messageSenderId = message.senderParticipantId as string | undefined;

        if (!messageSenderId) {
          // eslint-disable-next-line no-console
          console.warn('Message found without senderParticipantId:', message);
          return;
        }

        const senderParticipantDetails = conversationParticipants?.find((p) => p.participantId === messageSenderId);

        if (currentGroup && currentGroup.participantId === messageSenderId) {
          currentGroup.messages.push(message);
        } else {
          if (currentGroup) {
            newGroupedMessages.push(currentGroup);
          }
          currentGroup = {
            participantId: messageSenderId,
            author: senderParticipantDetails?.name ?? 'Unknown User',
            userRole: senderParticipantDetails?.participantType ?? 'BACK_OFFICE',
            isCurrentUser: senderParticipantDetails?.isCurrentUser ?? false,
            messages: [message],
          };
        }
      });

      if (currentGroup) {
        newGroupedMessages.push(currentGroup);
      }
      dispatch(setCurrentConversationMessages(newGroupedMessages));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  useEffect(() => {
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        if (activeConversation?.conversationId) {
          fetchMessages(activeConversation.conversationId, activeConversation.participants);
        }
      }, 10000); // 10 seconds
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (activeConversation?.conversationId) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [activeConversation?.conversationId, activeConversation?.participants, fetchMessages]);

  const updateConversationTitle = useCallback(
    async (conversationId: string, newTitle: string) => {
      if (!conversationId || !newTitle.trim()) {
        return;
      }
      setIsUpdatingTitle(true);
      try {
        const conversationToUpdate = conversations.find((c) => c.conversationId === conversationId);
        if (!conversationToUpdate) {
          setIsUpdatingTitle(false);
          throw new Error('Conversation not found for updating title');
        }

        const updatedConversation = await ConversationService.updateConversation(conversationId, {
          title: newTitle,
        });

        dispatch(
          setConversations(conversations.map((c) => (c.conversationId === conversationId ? updatedConversation : c)))
        );

        dispatch(setCurrentConversation(updatedConversation));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update conversation title:', error);
      } finally {
        setIsUpdatingTitle(false);
      }
    },
    [conversations, dispatch]
  );

  const updateConversationStatus = useCallback(
    async (
      conversationId: string,
      newStatus: MilesPosNotesIntegrationService.Components.Schemas.Conversation['status']
    ) => {
      if (!conversationId) {
        return;
      }
      setIsUpdatingStatus(true);
      try {
        const updatedConversation = await ConversationService.updateConversation(conversationId, {
          status: newStatus,
        });

        dispatch(
          setConversations(conversations.map((c) => (c.conversationId === conversationId ? updatedConversation : c)))
        );

        if (activeConversation?.conversationId === conversationId) {
          dispatch(setCurrentConversation(updatedConversation));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update conversation status:', error);
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [activeConversation, conversations, dispatch]
  );

  const createConversation = useCallback(
    async (title: string, firstMessage: string, activeFilters: FilterValues) => {
      // There are two cases of creation new conversation:
      // 1. User provided object type and human readable object reference id through filters
      // 2. User provided NO filters, but selected one of existing conversations
      let humanReadableObjectReferenceIdToUse = activeFilters.humanReadableObjectReferenceId;
      let objectTypeToUse =
        activeFilters.objectType as MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum;

      if (activeConversation) {
        const reference = getReferenceData(activeConversation);

        if (reference?.humanReadableObjectReferenceId && reference?.objectType) {
          humanReadableObjectReferenceIdToUse = reference.humanReadableObjectReferenceId;
          objectTypeToUse = reference.objectType;
        }
      }

      const metadata = await fetchObjectMetadata(humanReadableObjectReferenceIdToUse, objectTypeToUse);

      if (!metadata.systemObjectId) {
        debugLog({ message: 'System object id is empty', level: 'ERROR' });
        return;
      }

      const conversation = await ConversationService.createConversation({
        title,
        context: {
          representationId: objectTypeToUse,
          representationObjectId: metadata.systemObjectId,
        },
        firstMessage,
      });
      dispatch(setCurrentConversation(conversation));

      await fetchConversations(activeFilters);
    },
    [fetchConversations, dispatch, activeConversation]
  );

  const addMessageAndJoinIfNeeded = useCallback(
    async (
      currentActiveConversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation,
      messageText: string
    ) => {
      if (!currentActiveConversation?.conversationId || !messageText) {
        return { success: false };
      }

      let conversationToUse = currentActiveConversation;
      let joinedConversation = false;

      const currentUserIsParticipant = currentActiveConversation.participants?.some((p) => p.isCurrentUser);

      if (!currentUserIsParticipant) {
        try {
          const joinResponse = await ConversationService.joinConversation(currentActiveConversation.conversationId!);
          if (joinResponse && joinResponse.participantId) {
            const newParticipant: MilesPosNotesIntegrationService.Components.Schemas.Participant = {
              participantId: joinResponse.participantId,
              name: joinResponse.name,
              participantType: joinResponse.participantType,
              isCurrentUser: true,
            };

            const updatedParticipants = [...(currentActiveConversation.participants || []), newParticipant];
            const updatedConversation = {
              ...currentActiveConversation,
              participants: updatedParticipants,
            };

            dispatch(setCurrentConversation(updatedConversation));
            conversationToUse = updatedConversation;
            joinedConversation = true;
          } else {
            // eslint-disable-next-line no-console
            console.error('Failed to join conversation or joinResponse was invalid:', joinResponse);
            return { success: false };
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error joining conversation:', error);
          return { success: false };
        }
      }

      try {
        dispatch(setIsSendingMessage(true));
        await ConversationService.addMessage(conversationToUse.conversationId!, { message: messageText });
        await fetchMessages(conversationToUse.conversationId!, conversationToUse.participants);
        return { success: true, joined: joinedConversation };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to add message:', error);
        return { success: false };
      } finally {
        dispatch(setIsSendingMessage(false));
      }
    },
    [dispatch, fetchMessages]
  );

  return {
    createConversation,
    addMessageAndJoinIfNeeded,
    fetchMessages,
    fetchConversations,
    isUpdatingTitle,
    updateConversationTitle,
    isUpdatingStatus,
    updateConversationStatus,
  };
}
